/**
 * The café's own photographs — the single source for every image on the
 * site.
 *
 * All nine are client-supplied pictures of the real premises, which is why
 * they take priority over every stock stand-in they replaced. Each carries
 * its alt text alongside it so the description travels with the image and
 * cannot be re-written differently in two places.
 *
 * Several of these settle facts the copy previously guessed at — see
 * docs/registry.md, Decisions D16.
 */
export interface SiteImage {
  src: string
  alt: string
}

/**
 * The home-page hero, restored at the client's request.
 *
 * Held separately from IMAGES because it is the one illustration on the
 * site rather than a photograph of the premises — it is AI-generated, and
 * keeping it out of the photograph record stops it being reused as though
 * it were evidence of anything. The share image stays a real photograph
 * for the same reason. Registry D19.
 */
export const HERO_IMAGE = {
  src: 'https://cdn.magicpatterns.com/uploads/rp73EQG2PkFmCHJhURNFYr/Gemini_Generated_Image_j7rdbij7rdbij7rd.png',
  alt: 'Motorcycles parked outside the Low On Fuel café building',
} as const satisfies SiteImage

/**
 * The shop's shared product photograph: the merchandise rail inside the
 * café, with the tee, the cap and the snood all legible.
 *
 * It is the real picture for those three items. For the one-off reclaimed
 * pieces it stands in until each is photographed, and anything using it
 * that way carries `photoPending` so the page can say so rather than
 * letting a rail of t-shirts pass for a piston clock. Registry D22.
 */
export const PRODUCT_PLACEHOLDER = {
  src: 'https://cdn.magicpatterns.com/uploads/ayzjvN8hoH8cR57WXkntvV/Low_On_Cafe_03.webp',
  alt: 'Low On Fuel Café merchandise on a wire rail against the café\u2019s bare stone wall: navy tees printed with a yellow Brackenfield rider design, a black trucker cap and a Morf neck tube, with folded tees stacked on the table below.',
} as const satisfies SiteImage

export const IMAGES = {
  /** The signature shot: bikes, people, the deck and the valley beyond. */
  bikeNightCrowd: {
    src: 'https://cdn.magicpatterns.com/uploads/azD5cvKqyadRgwR3H7sGtG/Low_On_Cafe_07.webp',
    alt: 'Dozens of motorcycles parked along the roadside at Low On Fuel on a summer evening, with riders sitting out on the grass and standing on the timber deck above.',
  },

  /** Carries the café's own "Bike Night Every Friday" signage. */
  bikeNightSign: {
    src: 'https://cdn.magicpatterns.com/uploads/gGLSV4vjwHrAYdUGmJttjC/Low_On_Cafe_09.webp',
    alt: "Low On Fuel's painted 'Bike Night Every Friday' sign on the side of the timber deck, with motorcycles parked along the road behind and riders sitting on the grass.",
  },

  /** The front door, under the hanging roundel. */
  frontDoor: {
    src: 'https://cdn.magicpatterns.com/uploads/3UMETFEu8odWeZhBWHHHRp/Low_On_Cafe_12.webp',
    alt: 'The open front door of Low On Fuel Café in its gritstone porch, with the motorcycle roundel sign hanging above it and planted flowers either side.',
  },

  /** The building from the road, with the petrol-pump sign. */
  buildingFromRoad: {
    src: 'https://cdn.magicpatterns.com/uploads/niVH6Aixm516vSfxigNEig/Low_On_Cafe_01.webp',
    alt: "The gritstone café building seen from the road, with Low On Fuel's petrol-pump sign on a post and tables set out on the stone terrace.",
  },

  /** The deck, and the view that is the reason to sit on it. */
  deckView: {
    src: 'https://cdn.magicpatterns.com/uploads/8vssece4Kvn2iZf1ndFnYM/Low_On_Cafe_15.webp',
    alt: 'The raised timber deck at Low On Fuel, tables and stools set out in the sun, looking across open Derbyshire fields to the hills beyond.',
  },

  /** A stop, rather than a meal. Helmets down, drink poured. */
  helmetsOnTable: {
    src: 'https://cdn.magicpatterns.com/uploads/ipTzKKNFwP9SpgB3meHWdf/Low_On_Cafe_10.webp',
    alt: 'Two crash helmets resting on a timber table on the terrace beside a drink, with the road and open countryside behind.',
  },

  /** Stone wall, mustard paint, bus blind, bicycle on the wall. */
  interiorBar: {
    src: 'https://cdn.magicpatterns.com/uploads/8KdsbehtrBeBRCtoVTsJPj/Low_On_Cafe_13.webp',
    alt: 'Inside Low On Fuel: a vintage bicycle mounted on the bare stone wall beside a Kodak sign, a mustard wall carrying an old London bus destination blind, and mismatched tables and orange bar stools on the flagstone floor.',
  },

  /** The snug: helmets on the mantel, posters, the pinball-top table. */
  interiorSnug: {
    src: 'https://cdn.magicpatterns.com/uploads/3y2jrmvuyxkiV4g6hYNJrh/Low_On_Cafe_11.webp',
    alt: 'A corner of the café with crash helmets displayed on the mantelpiece, framed racing photographs and original Clash and Doors gig posters on the walls, and leather chairs around a table made from a pinball machine top.',
  },

  /** Memorabilia wall: McQueen, the moose, the TT frames. */
  interiorMemorabilia: {
    src: 'https://cdn.magicpatterns.com/uploads/2sCUa3rkDt29cgp7ijfngT/Low_On_Cafe_14.webp',
    alt: 'A wall inside the café hung with framed Isle of Man TT photographs beneath a Steve McQueen poster reading "Attack life, it\'s going to kill you anyway", with a carved moose head mounted above on a tree trunk.',
  },

  /**
   * The Fuel Stop itself, with the menu boards either side of the hatch.
   * This is the photograph the Food & Drink page previously had to caption
   * around, and it evidences the menu word for word.
   */
  fuelStopHatch: {
    src: 'https://cdn.magicpatterns.com/uploads/3vYJ7T3kY4dV1hiX3iF7KJ/Low_On_Cafe_08.webp',
    alt: "The Fuel Stop, a grey serving cabin lit by festoon bulbs with the Low On Fuel roundel above the hatch, a menu board mounted either side and a member of staff serving from the window.",
  },

  /** The ice cream counter, a whole side of the café the copy had missed. */
  iceCreamCounter: {
    src: 'https://cdn.magicpatterns.com/uploads/dSjuFrt47HBYefVgCsc4Bq/Low_On_Cafe_04.webp',
    alt: 'The ice cream counter inside the café, with two chilled display cabinets of gelato under a bare stone wall, a gilt mirror behind and a member of staff serving.',
  },

  /** What the ice cream actually looks like, and where you eat it. */
  iceCreamCone: {
    src: 'https://cdn.magicpatterns.com/uploads/2DrymoevJt5z4hc51sEePW/Low_On_Cafe_05.webp',
    alt: 'A two-scoop ice cream in a cone dipped in chocolate and hundreds and thousands, held up in front of the café terrace with the Derbyshire valley behind.',
  },

  /** The cold drinks, with a Triumph parked on the deck behind them. */
  icedCoffees: {
    src: 'https://cdn.magicpatterns.com/uploads/2p3iyUvHupk7APtzaj1Gsd/Low_On_Cafe_06.webp',
    alt: 'Two tall iced coffees topped with whipped cream and sauce on a timber table, with a Triumph motorcycle parked on the deck behind them.',
  },

  /* ---------------------------------------------------------------------
   * The inside of the building, photographed room by room.
   *
   * These arrived after the copy was written and they correct it in three
   * places: there is a full bar indoors as well as the hatch outside, the
   * hot drinks board and the cake price are both legible, and the interior
   * is five or six distinct rooms rather than the one the copy implied.
   * Registry D30.
   * ------------------------------------------------------------------- */

  /**
   * The main counter. THE SOURCE for the fact that the place is licensed:
   * beer taps, spirits, a wine fridge, the "Coffee Bar" sign, the cake
   * cabinet, and the chain-art guitars on the panelling.
   */
  theBar: {
    src: 'https://cdn.magicpatterns.com/uploads/iXRZQSReVjspbTvm2VfiKE/Low_On_Fuel_Internals_31.webp',
    alt: 'The main bar inside Low On Fuel: a pale blue panelled counter faced with guitars made from motorcycle chain, beer taps and a row of spirits behind, a cake cabinet and espresso machine to the left, and a chalkboard above reading "Life and beer are very similar. Chill for best results."',
  },

  /**
   * The hot drinks board, in the foreground and legible. The source for
   * the drinks list in `data/menu.ts`.
   */
  drinksBoard: {
    src: 'https://cdn.magicpatterns.com/uploads/qy77RwvgPG51r7yhFrfcuS/Low_On_Fuel_Internals_48.webp',
    alt: 'The coffee bar seen along its glass drinks board, which lists latte, cappuccino, flat white, americano, espresso, a choice of teas and Clipper hot chocolate, with two members of staff behind the counter and the room opening out beyond.',
  },

  /** A slice of cake in the cabinet. Cakes are £3.50, per the bar sign. */
  cakeSlice: {
    src: 'https://cdn.magicpatterns.com/uploads/6uhfHuaUWnhYTL9akSTdox/Low_On_Fuel_Internals_08.webp',
    alt: 'A thick slice of coffee and walnut cake on a paper-lined plate in the café’s glass cake cabinet, topped with buttercream, dark chocolate drizzle and crushed freeze-dried raspberries.',
  },

  /** The inglenook snug: patchwork wing chair, milk churn as a table. */
  snugFireside: {
    src: 'https://cdn.magicpatterns.com/uploads/knqghwBSo46vyDW7Lcp2wu/Low_On_Fuel_Internals_15.webp',
    alt: 'A small fireside room with bare gritstone walls and dark ceiling beams, a patchwork leather wing-back chair beside a green buttoned one, an old milk churn used as a table, and Persian rugs on the floor.',
  },

  /** The stone shelf and stools under the London bus blind. */
  barStoolsNook: {
    src: 'https://cdn.magicpatterns.com/uploads/uLAGxi15bALNHhNS5kPs5T/Low_On_Fuel_Internals_14.webp',
    alt: 'A nook with four orange velvet bar stools at a shelf built into the stone wall, a gilt mirror and framed racing photographs above, and an old London bus destination blind mounted on the mustard-yellow panelling to the left.',
  },

  /** The poster room: Kodak signs, festoon bulbs, Aintree Grand Prix. */
  posterRoom: {
    src: 'https://cdn.magicpatterns.com/uploads/mBzKrphPMJKdTHDvRTxFiT/Low_On_Fuel_Internals_38.webp',
    alt: 'A stone-walled room lit by bare festoon bulbs, hung with vintage Kodak advertising lanterns, a Dubbleware overalls sign, an Aintree British Grand Prix poster and a bicycle mounted above the radiator, with mismatched tables and velvet chairs below.',
  },

  /** The main dining room: tartan carpet, bikes on the walls, Shell pump. */
  diningRoom: {
    src: 'https://cdn.magicpatterns.com/uploads/agDH9cDCA6RBPaUejH5iyb/Low_On_Fuel_Internals_12.webp',
    alt: 'The long dining room at Low On Fuel, tartan carpet and gritstone walls, with two vintage motorcycles mounted high on either side, a restored Shell petrol pump by the door and tables of green leather chairs under brass chandeliers.',
  },

  /** The mounted motorcycle and Shell pump, close to. */
  mountedMotorcycle: {
    src: 'https://cdn.magicpatterns.com/uploads/dqZYuv4BpB1zB8AdCKZpbB/Low_On_Fuel_Internals_29.webp',
    alt: 'An orange and black veteran motorcycle mounted on the gritstone wall above the tables, with an enamel BP Benzol sign to the left and a cream Shell petrol pump standing beside the door.',
  },

  /** A Triumph engine, lit in a stone alcove. */
  engineAlcove: {
    src: 'https://cdn.magicpatterns.com/uploads/fVjQDAxXXxdrD6HTY13rae/Low_On_Fuel_Internals_40.webp',
    alt: 'A polished Triumph single-cylinder engine displayed and spotlit in a deep stone alcove, beside an old green cast-iron winch mounted on the wall.',
  },

  /**
   * The merchandise corner. Better evidence for the shop than the rail
   * shot: tees and a cap on hangers, helmets displayed, gig posters.
   */
  merchandiseCorner: {
    src: 'https://cdn.magicpatterns.com/uploads/pXjGK2LqGspUFG5NnEw6WC/Low_On_Fuel_Internals_43.webp',
    alt: 'A corner of the café with navy Low On Fuel tees and a cap hanging on wooden hangers against a mustard-yellow chimney breast, two painted crash helmets displayed in the alcove beside them, framed Clash and Doors posters on the walls and a tan leather sofa below.',
  },

  /** The beamed passage: window seat, inglenook, the stairs. */
  beamedPassage: {
    src: 'https://cdn.magicpatterns.com/uploads/k93VN9v7QUWaQT9ZZi7ezu/Low_On_Fuel_Internals_23.webp',
    alt: 'A low beamed passage inside the café with a deep stone inglenook on the left, a fitted timber window seat along the right wall under a mullioned window, and carpeted steps rising at the far end.',
  },

  /** Chess set out on a table, in the room off the dining room. */
  chessTable: {
    src: 'https://cdn.magicpatterns.com/uploads/iFQCqnGiMtzEqSZpnTmgfS/Low_On_Fuel_Internals_09.webp',
    alt: 'A room in the café with a carved chess set laid out ready on a round table, a red veteran motorcycle mounted on the wall above it and a second bike on the far wall, leather chairs at tables across a tartan carpet.',
  },

  /** The Dunlop sign, and the red bike above the tables. */
  dunlopSign: {
    src: 'https://cdn.magicpatterns.com/uploads/vL55B4VCEpvesAWdxT9ta4/Low_On_Fuel_Internals_26.webp',
    alt: 'A tall yellow enamel Dunlop sign propped against the gritstone wall beside a motorcycle chainring mounted as wall art, with a red veteran motorcycle hung above the tables beyond.',
  },

  /* ---------------------------------------------------------------------
   * Second interior set. Mostly further angles on rooms already
   * photographed, banked for the gallery — but three of them carry facts
   * the site did not have: the drinks board square-on with every price
   * legible, the corridor signed to a beer garden and a unisex toilet,
   * and the café's own "Please Mind Your Head & Step" notice.
   * Registry D34.
   * ------------------------------------------------------------------- */

  /**
   * THE SOURCE for every hot drink price. The first board shot had the
   * prices at too shallow an angle to read; this one is square-on and
   * every dotted leader is legible. The "All Cakes £3.50" chalk sign is
   * in frame too, which confirms the cake price a second way.
   */
  drinksBoardPrices: {
    src: 'https://cdn.magicpatterns.com/uploads/aehHTE4aMfFpjPsnHbgUGH/Low_On_Fuel_Internals_36.webp',
    alt: 'The coffee bar’s glass price board read square-on, listing latte, cappuccino, flat white and americano at £3.80, espresso and a choice of teas at £2.50 and Clipper hot chocolate at £3.50, with the cake cabinet and a chalk sign reading "All cakes £3.50" beside it.',
  },

  /**
   * THE SOURCE for the beer garden and the unisex toilet, both of which
   * are signed at the end of this corridor and neither of which the site
   * previously mentioned.
   */
  gardenCorridor: {
    src: 'https://cdn.magicpatterns.com/uploads/cRixtZDsGvBBY3snTJ9RUQ/Low_On_Fuel_Internals_32.webp',
    alt: 'A narrow stone-walled corridor lit by wall lamps and a skylight, tiled underfoot, leading to a glazed door with signs above reading "Beer Garden" and "Unisex Toilet".',
  },

  /**
   * The bar along its length. Carries the café's own "Please Mind Your
   * Head & Step" notice, which is exactly what the access copy says.
   */
  barLength: {
    src: 'https://cdn.magicpatterns.com/uploads/op9rriCwR1KVag6QJJLUuF/Low_On_Fuel_Internals_06.webp',
    alt: 'The bar seen along its length, with beer taps, a lit drinks fridge, wine on the shelves and chain-art guitars on the counter front, and a sign on the far wall reading "Please mind your head & step" above a stone step up into the next room.',
  },

  /** The cake cabinet, six slices in it. */
  cakeCabinet: {
    src: 'https://cdn.magicpatterns.com/uploads/hmrZnB7HKo7BziQR53RonM/Low_On_Fuel_Internals_20.webp',
    alt: 'Six thick slices of different cakes on white plates in the café’s glass counter cabinet, with a Shell sign and shelves of retro sweets and spirits behind.',
  },

  /** The Harley mounted on the gable end, above the side door. */
  gableMotorcycle: {
    src: 'https://cdn.magicpatterns.com/uploads/cxdSAAcXFF89PTK6cW1RzK/Low_On_Fuel_Internals_28.webp',
    alt: 'A silver vintage Harley-Davidson mounted on a timber platform high on the gritstone gable end of the café, against a clear blue sky.',
  },

  /** The bus-blind nook, from the other side. */
  busBlindNook: {
    src: 'https://cdn.magicpatterns.com/uploads/1aEpUgbM1eGypT51WRpUrZ/Low_On_Fuel_Internals_17.webp',
    alt: 'A corner of the café with a framed London bus destination blind on mustard-yellow panelling, four orange velvet stools at a stone shelf beneath a gilt mirror and framed racing photographs, and the coffee bar visible through a stone archway beyond.',
  },

  /** The bar front, square-on, with the roundel between the guitars. */
  barFront: {
    src: 'https://cdn.magicpatterns.com/uploads/2ZYMgq5nVngbaoWuvkzaEf/Low_On_Fuel_Internals_27.webp',
    alt: 'The bar counter seen square-on, two guitars made from motorcycle chain mounted either side of the Low On Fuel roundel on its panelled front, beer taps and shelves of spirits, wine and retro tins behind, with a member of staff serving.',
  },

  /** The dining room from the top of the steps. */
  diningRoomHigh: {
    src: 'https://cdn.magicpatterns.com/uploads/qp9AyvTTgH9YK7cga4jmAd/Low_On_Fuel_Internals_30.webp',
    alt: 'The dining room seen from the steps above it, tartan carpet between gritstone walls, a green veteran motorcycle mounted on one wall and a red one on the other, tables of green leather chairs and a carved chess set laid out ready on a round table.',
  },

  /** The poster room, wider, with the red bike against the wall. */
  posterRoomWide: {
    src: 'https://cdn.magicpatterns.com/uploads/tumtHZcVZFjL7gnEUoq1g7/Low_On_Fuel_Internals_37.webp',
    alt: 'A festoon-lit room with a Dubbleware overalls sign, a Kodak lantern and a military bicycle mounted on the stone wall, an Aintree British Grand Prix poster beside them, and a framed London bus blind on the yellow panelling opposite.',
  },

  /** The antiques passage: magazine rack, window seat, wing chairs. */
  antiquesPassage: {
    src: 'https://cdn.magicpatterns.com/uploads/uBGxBHLNNBABXAdcyvUMPP/Low_On_Fuel_Internals_41.webp',
    alt: 'A low beamed passage with a tall "Antiques" clock above a rack of magazines, a fitted pine window seat and Persian rugs to one side, and patchwork and green leather wing-back chairs in the room beyond.',
  },

  /** The snug from the doorway, Shopmobility sign above the fire. */
  snugDoorway: {
    src: 'https://cdn.magicpatterns.com/uploads/t2shczEBnDJb3jAWgjRngL/Low_On_Fuel_Internals_16.webp',
    alt: 'A small stone-walled snug with a patchwork leather wing-back chair and a green buttoned one either side of an old milk churn used as a table, a reclaimed Shopmobility sign mounted on the beam above the brick fireplace.',
  },

  /** The wood burner, and the games stacked beside it. */
  woodBurnerSnug: {
    src: 'https://cdn.magicpatterns.com/uploads/i5xWvDUx9pG9biVZD4Epxi/Low_On_Fuel_Internals_21.webp',
    alt: 'A beamed snug with a wood-burning stove set into a brick inglenook, a green leather wing-back chair beside it, board games and an STP oil drum stacked on the hearth, and a doorway signed to the toilet.',
  },

  /** The window seat under the Dubbleware sign. */
  windowSeat: {
    src: 'https://cdn.magicpatterns.com/uploads/r8xSnUsbeD5AZZ3Z89E8ru/Low_On_Fuel_Internals_02.webp',
    alt: 'A fitted timber window seat below a mullioned window looking out to the road, a yellow enamel Dubbleware overalls sign hung above it between two framed portraits of monkeys in Tudor dress, with small round tables in front.',
  },

  /** The coffee-sign room, orange bike on the wall. */
  coffeeSignRoom: {
    src: 'https://cdn.magicpatterns.com/uploads/qV7ZJq7MstEr6eg5JBpVLg/Low_On_Fuel_Internals_33.webp',
    alt: 'A beamed room with an orange veteran motorcycle mounted on the gritstone wall beside a large round "Fresh brewed coffee served daily" sign, tables of green leather chairs on tartan carpet and a stone inglenook beyond.',
  },

  /** The Dunlop sign from the room side, red bike above. */
  dunlopRoom: {
    src: 'https://cdn.magicpatterns.com/uploads/39Goe1RHiGLhHX1oJFoqsW/Low_On_Fuel_Internals_05.webp',
    alt: 'A tall yellow enamel Dunlop sign on the stone wall beside a chainring mounted as wall art, a red veteran motorcycle hung above the radiator, and tables of blue and green leather chairs across the tartan carpet beyond.',
  },

  /** The BP room, circus sign on the hearth. */
  bpRoom: {
    src: 'https://cdn.magicpatterns.com/uploads/9fvQNpimG7ydn5ZSQ7NA6G/Low_On_Fuel_Internals_22.webp',
    alt: 'A beamed room with a deep stone inglenook holding a painted King Bros circus sign, an enamel BP Benzol sign on the wall beyond, an orange veteran motorcycle mounted high up and long tables of leather chairs on tartan carpet.',
  },

  /** The bus-blind room with the red bicycle and the Kodak lantern. */
  busBlindRoom: {
    src: 'https://cdn.magicpatterns.com/uploads/4188XLaYQozPigh66pNHNP/Low_On_Fuel_Internals_35.webp',
    alt: 'A festoon-lit room with a military bicycle and a Kodak lantern on the stone wall, an Aintree Grand Prix poster below, a framed London bus destination blind on the yellow panelling and a red bicycle propped beneath it, with velvet stools at small tables.',
  },

  /** The upper room: window seat, gauges, the steps down. */
  upperWindowRoom: {
    src: 'https://cdn.magicpatterns.com/uploads/7q1brHX3YQ5xvegJhQvw2f/Low_On_Fuel_Internals_42.webp',
    alt: 'A heavily beamed upper room with a long fitted pine bench along one wall, a small round table and chairs at a window looking onto the garden, pressure gauges mounted on the yellow panelling and carpeted stone steps leading down.',
  },

  /** The BP corner: leather chairs, an engine on a glass table. */
  bpCorner: {
    src: 'https://cdn.magicpatterns.com/uploads/u2nRH4N6wE3eNGCSB7aD67/Low_On_Fuel_Internals_44.webp',
    alt: 'Two brown leather dining chairs in a stone corner of the café beneath a large enamel BP Benzol sign, with a motorcycle fuel tank and a stripped engine displayed on the hearth and a glass-topped table beside them.',
  },

  /* ---------------------------------------------------------------------
   * Final interior set. Two are the best evidence yet for the
   * merchandise — good enough to replace the shop's head image — and the
   * rest are further angles banked for the gallery. Registry D35.
   * ------------------------------------------------------------------- */

  /**
   * The merchandise, square-on and legible. The tee print reads "LOW ON
   * FUEL CAFÉ BRACKENFIELD", which the client has since confirmed as the
   * business location — the site now says Brackenfield throughout. Also
   * shows the cap, both painted helmets and the second tee design.
   */
  merchandiseWall: {
    src: 'https://cdn.magicpatterns.com/uploads/iLfuGs1HQuQJsuBUsBqE2y/Low_On_Fuel_Internals_01.webp',
    alt: 'Two navy Low On Fuel Café tees on wooden hangers either side of a mustard-yellow chimney breast, one printed "Low On Fuel Café Brackenfield" around a motorcycle, a cap hung above, two painted crash helmets displayed in the stone alcove beside them, framed Clash and Doors posters on the grey wall and a tan leather sofa below.',
  },

  /**
   * The whole lounge the merchandise hangs in. Better head image for the
   * shop than a corner crop: it shows the room someone actually walks
   * into, with the goods, the helmets and somewhere to sit.
   */
  merchandiseLounge: {
    src: 'https://cdn.magicpatterns.com/uploads/tCHASuWuzC6pi988JT3e7r/Low_On_Fuel_Internals_34.webp',
    alt: 'A bright lounge room in the café with Low On Fuel tees, a cap and two painted crash helmets displayed on the mustard-yellow wall at one end, a tan leather sofa and low table stacked with magazines, velvet and leather chairs at small tables, and framed gig and racing posters filling the walls around a window onto the road.',
  },

  /**
   * The bar square-on, with the two chain-art guitars and the roundel.
   * The clearest shot of the taps, the spirits and the card terminal.
   */
  barSquareOn: {
    src: 'https://cdn.magicpatterns.com/uploads/gMwNoJd1bqfq47wnSQJ9JB/Low_On_Fuel_Internals_47.webp',
    alt: 'The bar photographed square-on, two guitars made from motorcycle chain mounted either side of the Low On Fuel roundel on its panelled front, two banks of beer taps and a card terminal on the counter, spirits and wine on the shelves behind beneath a row of vintage tins and toys, and a member of staff serving.',
  },

  /**
   * The inglenook with the wood burner, the Shopmobility sign on the
   * beam and the "Bullshit Corner" print on the hearth. NOTE: the
   * Shopmobility sign is reclaimed decoration, not a facility.
   */
  bullshitCorner: {
    src: 'https://cdn.magicpatterns.com/uploads/8CFsBNJhTwaUyG6uLZfirc/Low_On_Fuel_Internals_25.webp',
    alt: 'A deep brick and stone inglenook with a wood-burning stove, a reclaimed Shopmobility direction sign mounted on the heavy timber beam above, a print reading "Welcome to Bullshit Corner" propped on the hearth, fire irons and a brass fender, with a green buttoned leather chair and a round table beside it.',
  },

  /** The gilt mirror wall, and the roundel reflected in it. */
  mirrorWall: {
    src: 'https://cdn.magicpatterns.com/uploads/29jjCU7dUqjAPfmGPFA4UK/Low_On_Fuel_Internals_11.webp',
    alt: 'A large carved gilt mirror on a white wall between four framed racing prints, reflecting the Low On Fuel roundel painted on the window opposite and the Dubbleware overalls sign hung above it, under a white beamed ceiling.',
  },

  /** The same wall, wider, with the teddy bear on the shelf. */
  mirrorWallWide: {
    src: 'https://cdn.magicpatterns.com/uploads/2dfSgTmAFgSzHKNZ7SR51S/Low_On_Fuel_Internals_13.webp',
    alt: 'The gilt mirror wall seen wider, four framed racing prints around it, a teddy bear sitting on the stone shelf below and brake fluid tins on a high shelf to the left, with the Low On Fuel roundel reflected in the glass.',
  },

  /** Blueberry and cream cake, in the cabinet. */
  cakeBlueberrySwirl: {
    src: 'https://cdn.magicpatterns.com/uploads/u9ANTXEtVUCtFrAeJbP4bq/Low_On_Fuel_Internals_03.webp',
    alt: 'A thick slice of sponge cake layered with blueberry compote and topped with swirled cream cheese icing, on a paper-lined plate in the café’s glass cake cabinet.',
  },

  /** Blueberry bakewell slice, in the cabinet. */
  cakeBlueberryBakewell: {
    src: 'https://cdn.magicpatterns.com/uploads/wi64t2ZWLjr626Bqg69CZV/Low_On_Fuel_Internals_39.webp',
    alt: 'A slice of blueberry bakewell dusted with icing sugar on a paper-lined plate in the café’s glass cake cabinet, whole blueberries set into the almond sponge above a layer of jam and shortcrust pastry.',
  },
} as const satisfies Record<string, SiteImage>
